import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getTypeConfig } from '../pages/NotificationsPage'
import {
  Bell,
  Check,
  X,
  Megaphone,
} from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { formatDateTime } from '../lib/format'
import { notificationsResponse } from '../lib/types'
import { onSocketEvent } from '../lib/socket'
import { useAuthStore } from '../stores/authStore'
import { SpaceInviteActions } from './SpaceInviteActions'

/**
 * Resolve the click-target link, REPAIRING the highlight deep-link from the
 * notification's structured `resourceType`/`resourceId` when the stored `link`
 * doesn't already carry a `?focus…` param. This makes click-to-highlight
 * link-format-independent: a task notification highlights on click even if its
 * link is just the bare list, and new notification kinds get the behaviour for
 * free once they set a resource. Links that already carry a focus param (the
 * common case) pass through untouched.
 */
function resolveHighlightLink(notification) {
  const link = notification?.link
  if (!link) return link
  if (/[?&]focus(Task|Comment|Attachment)=/.test(link)) return link
  const { resourceType, resourceId } = notification
  if (!resourceType || !resourceId) return link
  const enc = encodeURIComponent(resourceId)
  if (resourceType === 'task') {
    return `${link}${link.includes('?') ? '&' : '?'}focusTask=${enc}`
  }
  // Comments / attachments need the parent task in the link (`?task=…`) to open
  // the panel — only repair when it's already there.
  if (
    (resourceType === 'comment' || resourceType === 'attachment') &&
    /[?&]task=/.test(link)
  ) {
    const key = resourceType === 'comment' ? 'focusComment' : 'focusAttachment'
    return `${link}&${key}=${enc}`
  }
  return link
}

/** Resolve the panel-appropriate Notifications page URL from the
 *  signed-in user's role. Each panel has its own URL prefix:
 *    super_admin  → /super-admin/notifications
 *    company_admin → /notifications
 *    user          → /app/notifications
 */
function notificationsPathFor(role) {
  if (role === 'super_admin') return '/super-admin/notifications'
  if (role === 'company_admin') return '/notifications'
  return '/app/notifications'
}

/** Returns a rendered icon ELEMENT (not a component class) for a
 *  notification kind. Returning the element keeps the
 *  react-hooks/static-components rule happy at the call sites — JSX
 *  values can change every render without resetting children's state,
 *  but a component variable captured into a render-local `Icon`
 *  triggers the lint warning. */
function iconForKind(kind, size = 13) {
  // Reuse the full Notifications page's kind→icon map so the same notification
  // shows the same icon in the popup and the page (was a 4-prefix local map).
  const Cmp = getTypeConfig(String(kind ?? '')).icon ?? Bell
  return <Cmp size={size} />
}


export function NotificationsBell() {
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState(null)
  // Announcement.* rows have no link to navigate to, so clicking one
  // opens a modal with the full title/body/priority — Goal 1 ("clicking
  // the notification opens the full announcement"). Local state keeps
  // the modal scoped to the bell; the parent page route is unaffected.
  const [viewAnnouncement, setViewAnnouncement] = useState(null)
  const qc = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)
  const viewAllPath = notificationsPathFor(role)

  const notifsQuery = useQuery({
    queryKey: ['notifications', 'me'],
    queryFn: async () => {
      const res = await api.get('/notifications', { params: { limit: 25 } })
      return notificationsResponse.parse(res.data)
    },
    // Socket pushes drive realtime updates; the long-poll fallback
    // catches dropped connections without spamming the server.
    refetchInterval: 60_000,
    // Keep polling while the tab is backgrounded. Doubles as the
    // presence-heartbeat carrier — the auth middleware refreshes
    // `lastActiveAt` on every authorised request, so pausing polls
    // in background used to make signed-in users flip to "Offline"
    // on the login-activity logs (bug 0013264). Cost is one 60s
    // request from an idle tab, which is negligible.
    refetchIntervalInBackground: true,
  })

  useEffect(() => {
    const off = onSocketEvent('notification:new', () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'me'] })
    })
    return off
  }, [qc])

  const markReadMutation = useMutation({
    mutationFn: async (id) => {
      const res = await api.post(`/notifications/${id}/read`)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'me'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/notifications/read-all')
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications', 'me'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  useEffect(() => {
    if (!open) return
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setCoords({
      top: rect.bottom + window.scrollY + 8,
      right: window.innerWidth - rect.right,
    })
  }, [open])

  useEffect(() => {
    if (!open) return
    function onClick(e) {
      const target = e.target
      // Don't close the dropdown when the click lands on the
      // announcement modal — it portals outside `menuRef` but is
      // semantically nested under the bell.
      if (viewAnnouncement) return
      if (
        !menuRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    function onKey(e) {
      if (e.key !== 'Escape') return
      // Escape closes the modal first when one is open; only after
      // that does another Escape collapse the dropdown.
      if (viewAnnouncement) {
        setViewAnnouncement(null)
        return
      }
      setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, viewAnnouncement])

  const unread = notifsQuery.data?.unreadCount ?? 0
  const items = notifsQuery.data?.data ?? []

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        data-tour="tour-notifications"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications (${unread} unread)`}
        title="Notifications"
        className="relative h-9 w-9 rounded-full hover:bg-[color:var(--color-canvas)] flex items-center justify-center text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] cursor-pointer"
      >
        <Bell size={16} />
        {unread > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-[color:var(--color-danger)] text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ top: coords.top, ['--dd-right']: `${coords.right}px` }}
              // Mobile: span the viewport with 16px side margins (left-4 right-4,
              // width auto) so a panel anchored to a bell that ISN'T at the far
              // right edge can't overflow off-screen. Desktop (sm+): keep the
              // bell-anchored right offset + fixed 28rem width.
              className="fixed z-50 left-4 right-4 w-auto sm:left-auto sm:right-[var(--dd-right)] sm:w-[28rem] sm:max-w-[calc(100vw-32px)] rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-[color:var(--color-rule)] flex items-center justify-between">
                <p className="font-semibold text-[color:var(--color-ink-strong)] inline-flex items-center gap-2">
                  <Bell size={14} /> Notifications
                </p>
                {unread > 0 ? (
                  <button
                    type="button"
                    onClick={() => markAllReadMutation.mutate()}
                    disabled={markAllReadMutation.isPending}
                    className="text-xs font-medium text-[color:var(--color-accent-strong)] hover:underline cursor-pointer disabled:opacity-50"
                  >
                    Mark All Read
                  </button>
                ) : null}
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {notifsQuery.isLoading ? (
                  <p className="px-4 py-6 text-sm text-[color:var(--color-ink-muted)]">
                    Loading…
                  </p>
                ) : items.length === 0 ? (
                  <div className="px-4 py-10 text-center">
                    <Bell
                      size={24}
                      className="mx-auto mb-2 text-[color:var(--color-ink-subtle)]"
                    />
                    <p className="text-sm text-[color:var(--color-ink-strong)] font-medium">
                      You're all caught up
                    </p>
                    <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
                      Mentions, task updates, and report reminders will land here.
                    </p>
                  </div>
                ) : (
                  <ul className="divide-y divide-[color:var(--color-rule)]">
                    {items.map((n) => (
                      <NotificationRow
                        key={n.id}
                        notification={n}
                        onRead={() => markReadMutation.mutate(n.id)}
                        onClose={() => setOpen(false)}
                        onView={(notif) => setViewAnnouncement(notif)}
                      />
                    ))}
                  </ul>
                )}
              </div>
              {/* Footer link — opens the full Notifications page for
               *  triage with filters + pagination. Routes per panel
               *  based on the signed-in user's role. */}
              <div className="border-t border-[color:var(--color-rule)] bg-[color:var(--color-canvas)]">
                <Link
                  to={viewAllPath}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 text-center text-sm font-medium text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
                >
                  View all notifications
                </Link>
              </div>
            </div>,
            document.body,
          )
        : null}

      {/* Announcement viewer modal — opened when an announcement.* row
       *  is clicked. The row has no `link` to navigate to, so the full
       *  title/body/priority is shown inline. Backdrop click + Escape
       *  close the modal (Escape is handled in the dropdown's keydown
       *  effect above to coordinate with the dropdown's own Escape). */}
      {viewAnnouncement
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Announcement"
              className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setViewAnnouncement(null)
              }}
            >
              <div className="w-full max-w-lg rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-[color:var(--color-rule)] flex items-start gap-3">
                  <span
                    className="mt-0.5 h-8 w-8 rounded-full flex items-center justify-center shrink-0 bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]"
                    aria-hidden="true"
                  >
                    <Megaphone size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {viewAnnouncement.kind === 'announcement.high' ? (
                        <span
                          className="inline-flex items-center shrink-0 rounded-full bg-[color:var(--color-danger)] text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border border-transparent dark:border-white/10 dark:bg-white/[0.06] dark:text-rose-300"
                          aria-label="High priority announcement"
                        >
                          Priority
                        </span>
                      ) : null}
                      <p className="font-semibold text-[color:var(--color-ink-strong)]">
                        {viewAnnouncement.title}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-[color:var(--color-ink-subtle)]">
                      <span className="font-medium text-[color:var(--color-ink-muted)]">
                        {viewAnnouncement.createdByName ?? 'System'}
                      </span>
                      {' · '}
                      {formatDateTime(viewAnnouncement.createdAt)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setViewAnnouncement(null)}
                    aria-label="Close announcement"
                    className="text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] cursor-pointer"
                  >
                    <X size={16} />
                  </button>
                </div>
                {viewAnnouncement.body ? (
                  <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
                    <p className="text-sm text-[color:var(--color-ink-strong)] whitespace-pre-wrap break-words text-justify">
                      {viewAnnouncement.body}
                    </p>
                  </div>
                ) : null}
                <div className="px-5 py-3 border-t border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] flex justify-end">
                  <button
                    type="button"
                    onClick={() => setViewAnnouncement(null)}
                    className="text-sm font-medium text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

/** Two-letter initials from a full name. Returns null for empty / null
 *  input so the caller can fall back to the kind icon. */
function initials(name) {
  if (!name) return null
  const parts = String(name).trim().split(/\s+/)
  if (parts.length === 0 || parts[0] === '') return null
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? null
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

function NotificationRow({ notification, onRead, onClose, onView }) {
  const isUnread = !notification.readAt
  // Sender lifted from the joined `createdByName` field. Falls back to
  // "System" only when the row is a genuine system event (no human
  // actor) — gamification badges, scheduled report reminders, etc.
  const senderName = notification.createdByName ?? null
  const senderInitials = initials(senderName)
  // High-priority announcement broadcasts get an amber row tint plus
  // a left-accent border so they read as actually urgent. The backend
  // also injects a "Priority — " title prefix, but a text prefix alone
  // is easy to miss in a scrolling list.
  const isHighPriority = notification.kind === 'announcement.high'
  // Announcements are intentionally broadcast-shaped: even when a human
  // posted them (createdByName is set), the megaphone icon and a
  // distinct amber gradient must win so the row reads as a company-wide
  // broadcast rather than a person-to-person ping. Satisfies the
  // documented intent that `announcement.high also drives the megaphone
  // icon` and differentiates announcements from regular notifications.
  const isAnnouncement = notification.kind?.startsWith('announcement') ?? false
  const Inner = (
    <div
      className={
        'flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-[color:var(--color-card-hover)] ' +
        (isHighPriority
          ? 'border-l-4 border-[color:var(--color-danger)] bg-amber-50 dark:bg-amber-500/[0.10] '
          : '') +
        (isUnread && !isHighPriority ? 'bg-[color:var(--color-accent-tint)]' : '')
      }
    >
      <span
        className={
          'mt-0.5 h-7 w-7 rounded-full flex items-center justify-center shrink-0 text-[11px] font-semibold ' +
          // Announcement rows AND human-sender rows both use the SAME
          // ocean→navy avatar gradient (from-[#006CA5] to-[#02367B]) that the
          // UserMenu (top-right pill) and the shared <Avatar> use, so an
          // announcement's megaphone sits on the same blue background as a
          // person's avatar — consistent across the bell + notifications page.
          (isAnnouncement || senderInitials
            ? 'bg-gradient-to-br from-[#006CA5] to-[#02367B] text-white border border-transparent'
            : isUnread
              ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]'
              : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]')
        }
        aria-hidden="true"
      >
        {isAnnouncement
          ? iconForKind(notification.kind, 13)
          : (senderInitials ?? iconForKind(notification.kind, 13))}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {isHighPriority ? (
            <span
              className="inline-flex items-center shrink-0 rounded-full bg-[color:var(--color-danger)] text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border border-transparent dark:border-white/10 dark:bg-white/[0.06] dark:text-rose-300"
              aria-label="High priority announcement"
            >
              Priority
            </span>
          ) : null}
          <p className="text-sm text-[color:var(--color-ink-strong)] truncate">
            {notification.title}
          </p>
        </div>
        {notification.body ? (
          <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)] line-clamp-2">
            {notification.body}
          </p>
        ) : null}
        {/* Inline Accept / Decline for invitation notifications.
         *  Covers both Space and List invites — the component reads
         *  the link to figure out which endpoint to call. Hidden for
         *  every other kind. The component handles its own
         *  stopPropagation so clicking a button doesn't also trigger
         *  the row-level Link navigation. */}
        {notification.kind === 'space.invited' ||
        notification.kind === 'list.invited' ? (
          <div className="mt-2">
            <SpaceInviteActions notification={notification} />
          </div>
        ) : null}
        <p className="mt-1 text-xs text-[color:var(--color-ink-subtle)]">
          <span className="font-medium text-[color:var(--color-ink-muted)]">
            {senderName ?? 'System'}
          </span>
          {' · '}
          {formatDateTime(notification.createdAt)}
        </p>
      </div>
      {isUnread ? (
        <span className="mt-1 h-2 w-2 rounded-full bg-[color:var(--color-accent)] shrink-0" />
      ) : null}
    </div>
  )
  if (notification.link) {
    return (
      <li>
        <Link
          to={resolveHighlightLink(notification)}
          onClick={() => {
            if (isUnread) onRead()
            onClose()
          }}
        >
          {Inner}
        </Link>
      </li>
    )
  }
  return (
    <li>
      <button
        type="button"
        onClick={() => {
          if (isUnread) onRead()
          // Announcement rows have no `link` to navigate to, but the
          // body is line-clamp-2 in the dropdown so users couldn't read
          // the full broadcast. Open a modal with the full title, body
          // and priority badge (Goal 1). Other un-linked rows (e.g.
          // generic system pings) keep the existing "just mark read"
          // behavior so this isn't a behavior change for them.
          if (isAnnouncement && onView) onView(notification)
        }}
        className="w-full text-left"
      >
        {Inner}
      </button>
    </li>
  )
}

void Check
